import React, { useEffect, useState } from "react";
import CatalogTableC from "../../components/Catalogs/CatalogTableC";
import { Option } from "../../components/Atomos/DropDown/DDMultiple";
import { UseRegionList } from "../../hooks/Catalogs/useRegionListOptions";
import { UseCountryList } from "../../hooks/Catalogs";
import PaginatedTable from "../../components/Organismos/Table/PaginatedTable";
import useDataLoader2 from "../../hooks/useDataLoader2";
import ShapeEdit from "../../components/Atomos/Icons/ShapeEdit";
import { formatTimeSpanToString, getNameById } from "../../utils/functions";
import { getJurisdictionLabel } from "../../utils/jurisdiction/structuredJurisdiction";
import { useStatesList } from "../../hooks/Catalogs/useStates";
import { defaultPageSize } from "../../utils/const";
import { ServiceType, MembershipStatus } from "../../Enums/FirmEnum";
import Filters, {
  IFilterData,
} from "../../components/Catalogs/Filters/Filters";
import PersistentLink from "../../components/Moleculas/PersistentLink";
import { exportAllDataLoader2 } from "../../utils/exportAllDataLoader2";

const Firm: React.FC = () => {
  const [stateData, setStateData] = useState<any[]>([]);
  const { regionData } = UseRegionList();
  const { countryData } = UseCountryList();
  const { statesData } = useStatesList();
  const endpoint = "/firm";
  const {
    data,
    totalCount,
    fetchData,
    setFilter,
    sort,
    setSort,
    pagination,
    setPagination,
    filter,
    inputFilters,
    setInputFilters,
  } = useDataLoader2<any>({
    endpoint,
    defaultPageSize,
    defaultSorting: { field: "name", order: "asc" },
  });

  const columnsDef = React.useMemo(
    () => [
      {
        id: "count",
        header: "#",
        enableSorting: false,
        cell: ({ rowIndex }: { rowIndex: number; row: any; value: any }) => (
          <p> {rowIndex + 1 + pagination.pageIndex * pagination.pageSize} </p>
        ),
      },
      { id: "shortId", header: "Short ID" },
      { id: "name", header: "Name" },
      { id: "firm", header: "Firm Name" },
      { id: "membershipStatus", header: "Membership Status" },
      { id: "jurisdiction", header: "Jurisdiction" },
      { id: "jurisdictionGroup", header: "Jurisdiction Group" },
      { id: "state", header: "State" },
      { id: "country", header: "Country" },
      { id: "region", header: "Region" },
      { id: "membershipType", header: "Membership Type" },
      { id: "serviceType", header: "Service Type" },
      { id: "otherServiceType", header: "Other Service Type" },
      { id: "hasSecondary", header: "Has Secondary" },
      { id: "secondaryJurisdiction", header: "Secondary Jurisdiction" },
      { id: "acceptedEmailDomain", header: "Accepted Email Domain" },
      { id: "dateWithdrawn", header: "Date Withdrawn" },
      { id: "createdDate", header: "Created Date" },
      {
        id: "edit",
        header: "Edit",
        enableSorting: false,
        cell: ({
          row,
          rowIndex,
        }: {
          rowIndex: number;
          row: any;
          value: any;
        }) => (
          <PersistentLink
            className="edit"
            to={`Edit/${row.id}`}
            state={data[rowIndex]}
            persistingData={{
              pagination,
              sort,
              filter,
              inputFilters,
            }}
          >
            Edit
            <div className="editsvg">
              <ShapeEdit />
            </div>
          </PersistentLink>
        ),
      },
    ],
    [
      pagination.pageIndex,
      pagination.pageSize,
      sort,
      filter,
      inputFilters,
      data,
    ],
  );

  const [membershipStatus] = useState<Option[]>(
    Object.entries(MembershipStatus).map(([key, value]) => ({
      id: key,
      name: String(value),
    })),
  );
  const [serviceTypes] = useState<Option[]>(
    Object.entries(ServiceType).map(([key, value]) => ({
      id: key,
      name: String(value),
    })),
  );

  useEffect(() => {
    const states = statesData.filter((s: any) =>
      filter?.country?.value?.includes(s.country),
    );
    setStateData(states?.map((c: any) => ({ id: c.id, name: c.name || "" })));
  }, [filter?.country]);

  const formatAcceptedEmailDomainForDisplay = (value: unknown): string => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }
    if (typeof value === "string") {
      return value;
    }
    return "";
  };

  const formatData = (data: any[]) => {
    const formatedData = data.map((d: any) => {
      return {
        ...d,
        firm: d.firmName,
        jurisdiction: getJurisdictionLabel(d),
        jurisdictionGroup: d.jurisdictionGroupNames,
        region: d.regionNames,
        state: d.stateName,
        country: d.countryName,
        acceptedEmailDomain: formatAcceptedEmailDomainForDisplay(
          d.acceptedEmailDomain,
        ),
        createdDate: d.createdDate ? formatTimeSpanToString(d.createdDate) : "",
      };
    });
    return formatedData;
  };

  const filtersData: IFilterData[] = React.useMemo(
    () => [
      {
        name: "Region",
        fieldName: "regions",
        objectData: regionData?.map((r) => ({ id: r.id, name: r.name })) || [],
      },
      {
        name: "Country",
        objectData: countryData?.map((c) => ({ id: c.id, name: c.name })) || [],
      },
      {
        name: "State",
        objectData: stateData || [],
        disabled: !stateData?.length,
      },
      {
        name: "Service Type",
        fieldName: "serviceType",
        objectData: serviceTypes || [],
        comparison: "BY_NAME",
      },
      {
        name: "Name",
        type: "text",
        comparison: "CONTAINS",
      },
      {
        name: "Membership Status",
        fieldName: "membershipStatus",
        objectData: membershipStatus || [],
        comparison: "BY_NAME",
      },
    ],
    [regionData, countryData, stateData],
  );

  return (
    <>
      <div>
        <CatalogTableC
          title={`Firm Catalog`}
          catalogName={"FIRMS"}
          route={"Firm"}
          catalogShortName={"FIRM"}
          totalCount={totalCount}
        >
          <Filters
            filtersData={filtersData}
            filterValues={inputFilters}
            setFilterValues={setInputFilters}
            Search={() => fetchData()}
            clearSelections={() => fetchData(true)}
            setValueForQuery={setFilter}
          />
          <PaginatedTable
            data={data || []}
            columnsDef={columnsDef}
            formatData={formatData}
            sorting={sort}
            onSortingChange={setSort}
            totalCount={totalCount || 0}
            pagination={pagination}
            onPaginationChange={setPagination}
            endpoint={endpoint}
            onExportAll={() =>
              exportAllDataLoader2<any>({
                endpoint,
                filter,
                sort,
                pageSize: 1000,
              })
            }
          />
        </CatalogTableC>
      </div>
    </>
  );
};

export default Firm;
